import chanviLogo from './assets/chanvi.svg'
import heroFigure from './assets/chanvi-fig-01.png'
import triggersDiagram from './assets/chanvi-engagement-triggers.png'

const dimensions = [
  'Strategy and Business Architecture',
  'Organisation and Leadership',
  'Operations and Delivery',
  'Management and Governance',
  'Business Economics and Intelligence',
  'Digital and Technology Systems',
]

const services = [
  {
    number: '01',
    title: 'Innovation and Business Architecture',
    description: 'Shape the strategic logic and future direction of the enterprise.',
    items: [
      'Business model design and redesign',
      'Corporate strategy and growth roadmaps',
      'New venture and opportunity assessment',
      'Market entry and diversification strategy',
      'Business feasibility and strategic options',
    ],
  },
  {
    number: '02',
    title: 'Operations and Enterprise Transformation',
    description: 'Translate ambition into resilient operating structures and practices.',
    items: [
      'Operating-model design and redesign',
      'Process architecture and optimisation',
      'Operational excellence and productivity',
      'Organisational restructuring',
      'Transformation planning and implementation guidance',
    ],
  },
  {
    number: '03',
    title: 'Management and Leadership Systems',
    description: 'Create the mechanisms that turn accountability into performance.',
    items: [
      'Management systems and performance governance',
      'KPI frameworks and management reviews',
      'Decision rights and accountability structures',
      'Delegation and founder-dependency reduction',
      'Leadership and organisational capability development',
    ],
  },
  {
    number: '04',
    title: 'Strategy Analytics and Business Economics',
    description: 'Make economics, insight and evidence central to enterprise decisions.',
    items: [
      'Business health and performance diagnostics',
      'Profitability and cost-structure analysis',
      'Cash flow and working-capital improvement',
      'Unit economics and financial performance',
      'Business intelligence and management dashboards',
    ],
  },
  {
    number: '05',
    title: 'Digital, AI and Technology Advisory',
    description: 'Align technology decisions with enterprise priorities and capability.',
    items: [
      'Digital maturity and technology assessment',
      'AI opportunity identification and prioritisation',
      'Business process automation opportunities',
      'Enterprise systems and technology architecture',
      'Data strategy, MIS and technology governance',
    ],
  },
  {
    number: '06',
    title: 'Growth, Commercial Excellence and Expansion',
    description: 'Design the commercial systems required for disciplined expansion.',
    items: [
      'Go-to-market and commercial strategy',
      'Sales pipeline and CRM architecture',
      'Sales effectiveness and conversion improvement',
      'Customer segmentation and channel strategy',
      'Geographic expansion and multi-location scalability',
    ],
  },
]

const industries = [
  {
    label: 'Advisory & Services',
    title: 'Professional and Business Services',
    copy: 'Consulting firms, accounting and advisory businesses, technology service providers, and other professional service organisations.',
  },
  {
    label: 'Movement & Fulfilment',
    title: 'Logistics and Supply Chain',
    copy: 'Third-party logistics, freight forwarding, transportation, warehousing, fulfilment, distribution and integrated logistics businesses.',
  },
  {
    label: 'Production & Engineering',
    title: 'Manufacturing and Industrial Enterprises',
    copy: 'Manufacturers, industrial service providers, engineering businesses and enterprises managing complex production and distribution systems.',
  },
  {
    label: 'Care & Life Sciences',
    title: 'Healthcare and Life Sciences',
    copy: 'Healthcare service organisations, healthcare support businesses, pharmaceutical and life-sciences enterprises, and related service providers.',
  },
  {
    label: 'Software & Platforms',
    title: 'Technology and Digital Businesses',
    copy: 'Software companies, technology-enabled service businesses, digital platforms and organisations navigating technology-led growth.',
  },
  {
    label: 'Trade & Consumer',
    title: 'Consumer, Distribution and Commerce',
    copy: 'Trading and distribution companies, retail businesses, e-commerce operators and consumer-facing enterprises.',
  },
  {
    label: 'Growth & Diversification',
    title: 'Emerging and Diversifying Enterprises',
    copy: 'Established SMEs, founder-led businesses, growing enterprises and organisations entering new markets, business lines or operating models.',
  },
]

const engagementTriggers = [
  'The founder remains the primary decision-maker for too many activities.',
  'Revenue is growing, but profitability or cash flow remains under pressure.',
  'Sales performance depends on individual employees rather than a repeatable system.',
  'Departments operate in silos, with unclear ownership and conflicting priorities.',
  'Management lacks reliable, timely information for making decisions.',
  "Expansion plans are ahead of the organisation's operational readiness.",
  'Technology and AI investments are not translating into clear business value.',
  'New strategies are announced but not consistently implemented.',
  'Processes and structures are no longer suited to the next stage of growth.',
]

const engagements = [
  ['Assessment', 'Diagnostic Consulting', 'Understand constraints, root causes and opportunities.'],
  [
    'Decision Support',
    'Strategic Advisory',
    'Support consequential business decisions and strategic direction.',
  ],
  [
    'Defined Assignment',
    'Project-Based Consulting',
    'Address a defined business problem or transformation priority.',
  ],
  [
    'Staged Transformation',
    'Phased Transformation Consulting',
    'Progress from assessment and design to implementation guidance.',
  ],
  [
    'Embedded Leadership',
    'Fractional Leadership and Transformation Governance',
    'Provide defined strategic or management leadership support where appropriate.',
  ],
]

function Wordmark() {
  return (
    <div className="wordmark">
      <img
        src={chanviLogo}
        alt="Chanvi Ventures"
        width="150"
        height="150"
      />
    </div>
  )
}

function SystemVisual() {
  return (
    <figure className="system-visual">
      <img
        src={heroFigure}
        alt="Seven interacting enterprise dimensions: strategy, organisation, operations, management, economics, technology and people."
        width="1950"
        height="1444"
        fetchPriority="high"
      />
    </figure>
  )
}

function ContactLinks() {
  return (
    <address className="contact-links">
      <a href="mailto:hello@chanviventures.com">hello@chanviventures.com</a>
      <a href="tel:+918807473156">+91 8807473156</a>
    </address>
  )
}

function WhatsAppButton() {
  return (
    <a
      className="whatsapp-button"
      href="https://wa.me/918807473156"
      target="_blank"
      rel="noopener noreferrer"
      aria-label="Chat with Chanvi Ventures on WhatsApp"
    >
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.435 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z" />
      </svg>
    </a>
  )
}

function SectionHeading({
  eyebrow,
  title,
  intro,
}: {
  eyebrow: string
  title: string
  intro?: string
}) {
  return (
    <div className="section-heading">
      <p className="eyebrow">{eyebrow}</p>
      <h2>{title}</h2>
      {intro && <p className="section-intro">{intro}</p>}
    </div>
  )
}

export default function App() {
  return (
    <div className="site-shell">
      <header className="site-header">
        <div className="container header-inner">
          <Wordmark />
          <div className="coming-soon">
            <span aria-hidden="true" />
            Full Website Coming Soon
          </div>
        </div>
      </header>

      <main>
        <section className="hero">
          <div className="container hero-grid">
            <div className="hero-copy">
              <p className="eyebrow eyebrow--light">Enterprise transformation, designed as a system</p>
              <h1>
                Designing How Enterprises <em>Think, Operate <span>and</span> Evolve.</em>
              </h1>
              <p className="hero-lead">
                Chanvi Ventures helps enterprises rethink strategy, redesign organisational and
                operating systems, strengthen management architecture, and build the foundations
                for sustainable growth.
              </p>
              <p className="hero-support">
                We connect strategic intent with organisational design, operational performance,
                business economics, technology and disciplined execution.
              </p>
              <div className="status-line">
                <span className="status-line__mark" aria-hidden="true" />
                <span>Our digital presence is taking shape.</span>
              </div>
            </div>
            <SystemVisual />
          </div>
          <div className="hero-index" aria-hidden="true">
            <span>STRATEGY</span>
            <span>ORGANISATION</span>
            <span>OPERATIONS</span>
            <span>TECHNOLOGY</span>
          </div>
        </section>

        <section className="perspective section">
          <div className="container">
            <div className="perspective-grid">
              <div>
                <SectionHeading
                  eyebrow="Our Perspective"
                  title="Enterprise Performance Is a System. Not an Isolated Function."
                />
                <div className="perspective-copy">
                  <p>
                    Growth creates complexity. New customers change operating requirements.
                    Expansion changes organisational demands. Technology changes how work is
                    performed. Financial decisions influence capacity, capability and risk.
                  </p>
                  <p>
                    Chanvi Ventures examines these relationships together, helping businesses
                    identify structural constraints, align critical systems and design the next
                    stage of enterprise development.
                  </p>
                </div>
              </div>
              <div className="dimension-map">
                <p className="dimension-map__core">Enterprise Performance</p>
                <ol className="dimension-list">
                  {dimensions.map((dimension, index) => (
                    <li key={dimension}>
                      <span>0{index + 1}</span>
                      {dimension}
                    </li>
                  ))}
                </ol>
              </div>
            </div>
          </div>
        </section>

        <section className="services section">
          <div className="container">
            <SectionHeading
              eyebrow="Our Services"
              title="From Strategic Clarity to Enterprise Transformation."
              intro="Our consulting work addresses the business model, organisational structures, operating systems and management capabilities that determine how an enterprise performs and scales."
            />
            <div className="services-grid">
              {services.map((service) => (
                <article className="service-card" key={service.title}>
                  <div className="service-card__top">
                    <span className="service-number">{service.number}</span>
                    <span className="service-line" aria-hidden="true" />
                  </div>
                  <h3>{service.title}</h3>
                  <p>{service.description}</p>
                  <ul>
                    {service.items.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="industries section">
          <div className="container">
            <div className="industries-header">
              <SectionHeading
                eyebrow="Industries We Support"
                title="Different Industries. Connected Enterprise Challenges."
              />
              <p className="section-intro">
                Every industry has its own operating realities. Our consulting approach considers
                the commercial, organisational, operational, economic and technological
                characteristics of each business rather than applying a one-size-fits-all model.
              </p>
            </div>
            <div className="industries-grid">
              {industries.map((industry, index) => (
                <article className={`industry-item industry-item--${index + 1}`} key={industry.title}>
                  <span className="industry-index">{industry.label}</span>
                  <div>
                    <h3>{industry.title}</h3>
                    <p>{industry.copy}</p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="triggers section">
          <div className="container">
            <SectionHeading
              eyebrow="What Clients Engage Us to Address"
              title="When Growth Outpaces the Systems That Support It."
            />
            <img
              className="triggers-diagram"
              src={triggersDiagram}
              alt="Growing complexity passing through a bottleneck and becoming a coherent operating system."
              width="2200"
              height="760"
              loading="lazy"
            />
            <ol className="triggers-grid">
              {engagementTriggers.map((trigger, index) => (
                <li key={trigger}>
                  <span>{String(index + 1).padStart(2, '0')}</span>
                  <p>{trigger}</p>
                </li>
              ))}
            </ol>
            <p className="triggers-disclaimer">
              These are possible engagement triggers, not universal diagnoses. Each client
              situation requires a contextual assessment.
            </p>
            <blockquote className="triggers-quote">
              <span aria-hidden="true">“</span>
              <p>
                The question is not only where the business wants to go. It is whether the
                enterprise is designed to get there.
              </p>
            </blockquote>
          </div>
        </section>

        <section className="engagement section">
          <div className="container">
            <div className="engagement-intro">
              <SectionHeading
                eyebrow="How We Engage"
                title="Engagement Designed Around the Business Challenge."
              />
              <p className="section-intro">
                Not every business needs the same level of intervention. The appropriate
                engagement depends on the scale of the challenge, the decisions involved and the
                capabilities required.
              </p>
            </div>
            <ol className="engagement-list">
              {engagements.map(([label, title, copy]) => (
                <li key={title}>
                  <span className="engagement-number">{label}</span>
                  <div>
                    <h3>{title}</h3>
                    <p>{copy}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </section>

        <section className="closing">
          <div className="closing-geometry" aria-hidden="true" />
          <div className="container closing-inner">
            <p className="eyebrow eyebrow--light">The next enterprise chapter</p>
            <h2>Your Next Stage of Growth Needs More Than a Plan.</h2>
            <p className="closing-statement">
              It needs the right strategy, organisational architecture, management systems and
              execution discipline.
            </p>
            <div className="closing-note">
              <span aria-hidden="true" />
              Our full corporate website is under development. We welcome enquiries in the
              meantime.
            </div>
            <ContactLinks />
          </div>
        </section>
      </main>

      <footer className="footer">
        <div className="container footer-main">
          <Wordmark />
          <p>Innovation, Operations, Management and Strategy Analytics &amp; Consulting.</p>
        </div>
        <div className="container footer-bottom">
          <span>© 2026 Chanvi Ventures. All rights reserved.</span>
          <ContactLinks />
          <span>Designing how enterprises evolve.</span>
        </div>
      </footer>
      <WhatsAppButton />
    </div>
  )
}
